let homeStatsRailInstances = [];

function setupHomeStatsRail(track) {
  if (!(track instanceof HTMLElement)) {
    return { destroy() {} };
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let autoScrollTimer = 0;
  let firstTickTimer = 0;
  let resumeTimer = 0;
  let loopWidth = 0;
  let wrapTicking = false;

  const getGap = () => {
    const trackStyles = window.getComputedStyle(track);
    return Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0');
  };

  const getBaseCards = () =>
    Array.from(track.children).filter(
      (node) => node instanceof HTMLElement && !node.hasAttribute('data-stats-clone')
    );

  const getNaturalTrackWidth = () => {
    const cards = getBaseCards();
    if (!cards.length) {
      return 0;
    }

    const gap = getGap();
    const cardsWidth = cards.reduce((sum, card) => sum + card.getBoundingClientRect().width, 0);
    return cardsWidth + gap * Math.max(cards.length - 1, 0);
  };

  const hasClones = () => track.querySelector('[data-stats-clone]') instanceof HTMLElement;

  const clearClones = () => {
    const clones = track.querySelectorAll('[data-stats-clone]');
    clones.forEach((clone) => clone.remove());
    loopWidth = 0;
  };

  const ensureClones = () => {
    if (hasClones()) {
      if (loopWidth <= 0) {
        loopWidth = getNaturalTrackWidth();
      }
      return;
    }

    const cards = getBaseCards();
    if (cards.length < 2) {
      loopWidth = 0;
      return;
    }

    cards.forEach((card) => {
      const clone = card.cloneNode(true);
      if (!(clone instanceof HTMLElement)) {
        return;
      }

      clone.setAttribute('data-stats-clone', '1');
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    loopWidth = getNaturalTrackWidth();
  };

  const normalizeLoopPosition = () => {
    if (loopWidth <= 0) {
      return;
    }

    if (track.scrollLeft >= loopWidth) {
      track.scrollLeft -= loopWidth;
    }
  };

  const queueNormalizeLoopPosition = () => {
    if (wrapTicking) {
      return;
    }

    wrapTicking = true;
    requestAnimationFrame(() => {
      normalizeLoopPosition();
      wrapTicking = false;
    });
  };

  const getStep = () => {
    const firstCard = getBaseCards()[0];
    if (firstCard instanceof HTMLElement) {
      return Math.max(120, Math.round(firstCard.getBoundingClientRect().width + getGap()));
    }

    return Math.max(160, Math.round(track.clientWidth * 0.52));
  };

  const hasNaturalOverflow = () => getNaturalTrackWidth() - track.clientWidth > 8;
  const isTabletOrDown = () => window.matchMedia('(max-width: 1023px)').matches;
  const shouldAutoScroll = () => isTabletOrDown() && !prefersReducedMotion.matches && hasNaturalOverflow();

  const configureRailMode = () => {
    if (shouldAutoScroll()) {
      ensureClones();
      track.classList.add('is-auto-loop');
      queueNormalizeLoopPosition();
      return;
    }

    track.classList.remove('is-auto-loop');
    clearClones();
    track.scrollLeft = 0;
  };

  const advance = () => {
    if (!shouldAutoScroll()) {
      return;
    }

    if (loopWidth <= 0) {
      return;
    }

    const step = getStep();
    track.scrollBy({ left: step, behavior: 'smooth' });

    window.setTimeout(() => {
      normalizeLoopPosition();
    }, 560);
  };

  const stop = () => {
    if (autoScrollTimer) {
      window.clearInterval(autoScrollTimer);
      autoScrollTimer = 0;
    }

    if (firstTickTimer) {
      window.clearTimeout(firstTickTimer);
      firstTickTimer = 0;
    }
  };

  const clearResume = () => {
    if (resumeTimer) {
      window.clearTimeout(resumeTimer);
      resumeTimer = 0;
    }
  };

  const start = () => {
    stop();
    configureRailMode();

    if (!shouldAutoScroll()) {
      return;
    }

    firstTickTimer = window.setTimeout(() => {
      advance();
      autoScrollTimer = window.setInterval(advance, 2800);
      firstTickTimer = 0;
    }, 900);
  };

  const scheduleResume = () => {
    clearResume();
    if (!shouldAutoScroll()) {
      return;
    }

    resumeTimer = window.setTimeout(() => {
      start();
    }, 2200);
  };

  const pauseForInteraction = () => {
    stop();
    scheduleResume();
  };

  const handleResize = () => {
    start();
  };

  const handleTrackScroll = () => {
    if (loopWidth <= 0) {
      return;
    }

    queueNormalizeLoopPosition();
  };

  track.addEventListener('pointerdown', pauseForInteraction, { passive: true });
  track.addEventListener('touchstart', pauseForInteraction, { passive: true });
  track.addEventListener('wheel', pauseForInteraction, { passive: true });
  track.addEventListener('mouseenter', pauseForInteraction);
  track.addEventListener('mouseleave', scheduleResume);
  track.addEventListener('scroll', handleTrackScroll, { passive: true });

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);

  start();

  return {
    destroy() {
      stop();
      clearResume();

      track.removeEventListener('pointerdown', pauseForInteraction);
      track.removeEventListener('touchstart', pauseForInteraction);
      track.removeEventListener('wheel', pauseForInteraction);
      track.removeEventListener('mouseenter', pauseForInteraction);
      track.removeEventListener('mouseleave', scheduleResume);
      track.removeEventListener('scroll', handleTrackScroll);

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);

      track.classList.remove('is-auto-loop');
      clearClones();
    },
  };
}

function destroyHomeStatsRails() {
  homeStatsRailInstances.forEach((instance) => instance.destroy());
  homeStatsRailInstances = [];
}

function initHomeStatsRails() {
  destroyHomeStatsRails();

  const rails = Array.from(document.querySelectorAll('.crms-home-stats-scroll'));
  if (!rails.length) {
    return;
  }

  homeStatsRailInstances = rails.map((rail) => setupHomeStatsRail(rail));
}

initHomeStatsRails();
document.addEventListener('turbo:load', initHomeStatsRails);
document.addEventListener('turbo:before-cache', destroyHomeStatsRails);
